import React, { forwardRef, HTMLAttributes, ReactNode, Ref } from 'react';
import styled, { css } from 'styled-components';
import { coreTokens } from '../../core';

type TextVariants = {
	displayXLarge: string;
	displayLarge: string;
	displayMedium: string;
	displaySmall: string;
	headingXLarge: string;
	headingLarge: string;
	headingMedium: string;
	headingSmall: string;
	headingXSmall: string;
	bodyLargeRegular: string;
	bodyLargeSemiBold: string;
	bodyRegular: string;
	bodySemiBold: string;
	bodySmallRegular: string;
	bodySmallSemiBold: string;
	labelXLargeRegular: string;
	labelXLargeSemiBold: string;
	labelLargeRegular: string;
	labelLargeSemiBold: string;
	labelRegular: string;
	labelSemiBold: string;
	labelSmallRegular: string;
	labelSmallSemiBold: string;
	labelXSmallRegular: string;
	labelXSmallSemiBold: string;
};

type TextElementType = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span';

const textVariants = {
	displayXLarge: coreTokens.text.display.xLarge,
	displayLarge: coreTokens.text.display.large,
	displayMedium: coreTokens.text.display.medium,
	displaySmall: coreTokens.text.display.small,
	headingXLarge: coreTokens.text.heading.xLarge,
	headingLarge: coreTokens.text.heading.large,
	headingMedium: coreTokens.text.heading.medium,
	headingSmall: coreTokens.text.heading.small,
	headingXSmall: coreTokens.text.heading.xSmall,
	bodyLargeRegular: coreTokens.text.body.largeRegular,
	bodyLargeSemiBold: coreTokens.text.body.largeSemiBold,
	bodyRegular: coreTokens.text.body.regular,
	bodySemiBold: coreTokens.text.body.semiBold,
	bodySmallRegular: coreTokens.text.body.smallRegular,
	bodySmallSemiBold: coreTokens.text.body.smallSemiBold,
	labelXLargeRegular: coreTokens.text.label.xLargeRegular,
	labelXLargeSemiBold: coreTokens.text.label.xLargeSemiBold,
	labelLargeRegular: coreTokens.text.label.largeRegular,
	labelLargeSemiBold: coreTokens.text.label.largeSemiBold,
	labelRegular: coreTokens.text.label.regular,
	labelSemiBold: coreTokens.text.label.semiBold,
	labelSmallRegular: coreTokens.text.label.smallRegular,
	labelSmallSemiBold: coreTokens.text.label.smallSemiBold,
	labelXSmallRegular: coreTokens.text.label.xSmallRegular,
	labelXSmallSemiBold: coreTokens.text.label.xSmallSemiBold,
};

interface StyledElementProps {
	/**
	 * Use to set color of the text.
	 * @default inherit
	 */
	$color?: string;

	/**
	 * Visual representation of the text.
	 */
	$variant: keyof TextVariants;
}

const TextElement = styled.span<StyledElementProps>`
	${({ $variant }) =>
		$variant &&
		css`
			font: ${textVariants[$variant]};
		`}
	${({ $color }) =>
		$color &&
		css`
			color: ${$color};
		`}

	&[data-preserve-line-breaks] {
		white-space: pre-line;
	}

	&[data-truncate] {
		display: block;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
	}

	&:focus {
		outline: 0;
	}
`;

interface TextProps extends HTMLAttributes<HTMLElement> {
	/**
	 * The text to display.
	 */
	children: ReactNode;

	/**
	 * Use to specify which HTML element to be rendered.
	 * @default span
	 */
	as?: TextElementType;

	/**
	 * Use to preserve line breaks. Otherwise line breaks will be ignored.
	 */
	preserveLineBreaks?: boolean;

	/**
	 * Use to truncate overflow text with ellipsis.
	 */
	truncate?: boolean;

	/**
	 * Use to set color of the text.
	 * @default inherit
	 */
	color?: string;

	/**
	 * Visual representation of the text.
	 */
	variant: keyof TextVariants;
}

interface PTextProps extends TextProps {
	/**
	 * @default true
	 */
	preserveLineBreaks?: boolean;
}

type ForwardRefType<T = TextProps> = React.ForwardRefExoticComponent<T & React.RefAttributes<HTMLElement>>;

interface TextComponent extends ForwardRefType {
	h1: ForwardRefType;
	h2: ForwardRefType;
	h3: ForwardRefType;
	h4: ForwardRefType;
	h5: ForwardRefType;
	h6: ForwardRefType;
	p: ForwardRefType<PTextProps>;
}

/**
 * # Cellula:Text
 * Component to render text-elements.
 */
function CellulaText(
	{ children, as: asElement = 'span', color = 'inherit', variant, preserveLineBreaks, truncate, ...rest }: TextProps,
	ref: Ref<HTMLElement>,
): JSX.Element {
	return (
		<TextElement
			as={asElement}
			$color={color}
			$variant={variant}
			data-preserve-line-breaks={preserveLineBreaks || undefined}
			data-truncate={truncate || undefined}
			ref={ref}
			{...rest}
		>
			{children}
		</TextElement>
	);
}

function CellulaTextH1({ children, ...rest }: TextProps, ref: Ref<HTMLElement>): JSX.Element {
	return (
		<Text as='h1' ref={ref} {...rest}>
			{children}
		</Text>
	);
}

function CellulaTextH2({ children, ...rest }: TextProps, ref: Ref<HTMLElement>): JSX.Element {
	return (
		<Text as='h2' ref={ref} {...rest}>
			{children}
		</Text>
	);
}

function CellulaTextH3({ children, ...rest }: TextProps, ref: Ref<HTMLElement>): JSX.Element {
	return (
		<Text as='h3' ref={ref} {...rest}>
			{children}
		</Text>
	);
}

function CellulaTextH4({ children, ...rest }: TextProps, ref: Ref<HTMLElement>): JSX.Element {
	return (
		<Text as='h4' ref={ref} {...rest}>
			{children}
		</Text>
	);
}

function CellulaTextH5({ children, ...rest }: TextProps, ref: Ref<HTMLElement>): JSX.Element {
	return (
		<Text as='h5' ref={ref} {...rest}>
			{children}
		</Text>
	);
}

function CellulaTextH6({ children, ...rest }: TextProps, ref: Ref<HTMLElement>): JSX.Element {
	return (
		<Text as='h6' ref={ref} {...rest}>
			{children}
		</Text>
	);
}

function CellulaTextP({ children, preserveLineBreaks = true, ...rest }: PTextProps, ref: Ref<HTMLElement>): JSX.Element {
	return (
		<Text as='p' preserveLineBreaks={preserveLineBreaks} ref={ref} {...rest}>
			{children}
		</Text>
	);
}

/* ########################################################################## */
/* ############################### PUBLIC API ############################### */
/* ########################################################################## */

/**
 * A component to display a text.
 */
const Text = forwardRef(CellulaText) as TextComponent;
Text.h1 = forwardRef(CellulaTextH1);
Text.h2 = forwardRef(CellulaTextH2);
Text.h3 = forwardRef(CellulaTextH3);
Text.h4 = forwardRef(CellulaTextH4);
Text.h5 = forwardRef(CellulaTextH5);
Text.h6 = forwardRef(CellulaTextH6);
Text.p = forwardRef(CellulaTextP);

export type { TextComponent, TextProps };
export { Text };
